import { Constraint, ConstraintFragment, OfferFragment } from '@occc/graphql';
import { PartialOfferFragment } from '@occc-app/offerValidationService/types';
import { offerValidationService } from '@occc-app/offerValidationService';
import {
  CONSTRAINT_TYPE_NAMES_TO_BLOCKS_MAPPING,
  ConstraintBlockId,
  ConstraintBlocksVisibility,
} from './constraints-section.constants';

export const getConstraintsByType = <T extends ConstraintFragment['__typename']>(
  type: T,
  constraints: ConstraintFragment[],
): Extract<ConstraintFragment, { __typename: T }>[] => {
  return constraints.filter(
    (c): c is Extract<ConstraintFragment, { __typename: T }> => c.__typename === type,
  );
};

const getConstraintBlocksVisibilityByTypeNames = (
  constraintTypeNames: Constraint['__typename'][],
): ConstraintBlocksVisibility => {
  const set = new Set(constraintTypeNames);
  const visibility = {} as ConstraintBlocksVisibility;
  (Object.keys(CONSTRAINT_TYPE_NAMES_TO_BLOCKS_MAPPING) as ConstraintBlockId[]).forEach(
    (blockId) => {
      visibility[blockId] = CONSTRAINT_TYPE_NAMES_TO_BLOCKS_MAPPING[blockId].some((v) =>
        set.has(v),
      );
    },
  );

  // Both channels available means the offer is configured through the combined
  // Fulfillment Channel control, which replaces the single-channel blocks.
  const isFulfillmentChannelSupported =
    !!visibility.OrderAheadOnlyConstraint && !!visibility.InStoreOnlyConstraint;

  visibility.FulfillmentChannelConstraint = isFulfillmentChannelSupported;
  visibility.OrderAheadOnlyConstraint =
    visibility.OrderAheadOnlyConstraint && !isFulfillmentChannelSupported;
  visibility.InStoreOnlyConstraint =
    visibility.InStoreOnlyConstraint && !isFulfillmentChannelSupported;

  return visibility;
};

export const getConstraintBlocksVisibility = (
  offer: OfferFragment | PartialOfferFragment,
): ConstraintBlocksVisibility | undefined => {
  const schema = offerValidationService.getSchema(offer);

  if (schema) {
    return getConstraintBlocksVisibilityByTypeNames(schema.constraints);
  }

  // fallback for invalid offers
  if (!('constraints' in offer) || !Array.isArray(offer.constraints)) {
    return undefined;
  }
  const constraints = offer.constraints.map((v) => v.__typename);
  return getConstraintBlocksVisibilityByTypeNames(constraints);
};

export const filterConstraintBlocksVisibility = (
  allowedBlockIds: ConstraintBlockId[],
  visibility?: ConstraintBlocksVisibility,
): ConstraintBlocksVisibility | undefined => {
  if (!visibility) {
    return undefined;
  }
  const next: ConstraintBlocksVisibility = {};
  for (const id of allowedBlockIds) {
    if (id in visibility && visibility[id] !== undefined) {
      next[id] = visibility[id];
    }
  }
  return Object.keys(next).length > 0 ? next : undefined;
};
